import { FC, useCallback, useContext, useMemo } from 'react';
import { Avatar, Box, Button, Card, Chip, Grid, Stack, Typography } from '@mui/material';
import { CreditCard, Done, LocalShipping, Star } from '@mui/icons-material';

import { useDictionary } from '@cvt/hooks/useDictionary';
import { DialogContext } from '@cvt/contexts';

import circleBg from '@shared/images/circle_bg.svg';

import { useTributeWithEnabledOption } from '@modules/Tributes/hooks/useTribute';
import { BodyLoading } from '@cvt/components/layout/BodyLoading';
import HeartPulse from './HeartPulse';


type Props = {
    tribute?: Tributes.Tribute;
    tributeId?: number | string;
};

export const CreateTributeCard: FC<Props> = ({ tribute: tributeProp, tributeId }) => {
  const dictionary = useDictionary();
  const { openDialog } = useContext(DialogContext);
  const { tribute: fetchedTribute, isLoading } = useTributeWithEnabledOption(
    { id: tributeId as number | string },
    { enabled: !!tributeId },
  );

  const tribute = useMemo(() => tributeProp ?? fetchedTribute, [tributeProp, fetchedTribute]);

  const onCreateCard = useCallback(() => {
    if (!tribute) return;
    openDialog('createTributeCard', { tribute });
  }, [openDialog, tribute]);

  if (isLoading) return <BodyLoading />;

  return (
    <Card
      sx={{
        position: 'relative',
        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.08)',
        border: '1px solid #fcdcda',
        borderRadius: '12px',
        py: 5,
        px: 4,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0.05,
          backgroundImage: `url("${circleBg}")`,
          backgroundSize: '60px 60px',
          backgroundRepeat: 'repeat',
        }}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="center" gap={2} position="relative">
        <Avatar
          sx={{
            width: 64,
            height: 64,
            backgroundColor: '#feeeed',
          }}
          slots={{
            fallback:() => <HeartPulse size={24} />,
          }}
        />
        <Typography variant="h2" fontSize={36} fontWeight={700}>
          { dictionary.tributes.edit.card.title }
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="center" mt={4} position="relative">
        <Chip
          label={dictionary.tributes.edit.card.included}
          icon={<Done/>}
          color="success"
          sx={{
            px: 2,
            fontWeight: 500,
            fontSize: 14,
            height: 'auto',
            py: 0.5,
            '& .MuiChip-label': {
              whiteSpace: 'wrap',
            },
          }}
        />
      </Stack>
      <Typography variant="body1" textAlign="center" fontSize={18} fontWeight={700} color="text.secondary" mt={3} position="relative">
        { dictionary.tributes.edit.card.subtitle }
      </Typography>
      <Typography variant="body1" textAlign="center" fontSize={18} color="text.secondary" mt={3} position="relative">
        { dictionary.tributes.edit.card.subtitle2nd }
      </Typography>
      <Grid container spacing={2} mt={4} position="relative">
        <Grid size={4}>
          <Stack alignItems="center" gap={2}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                backgroundColor: '#feeeed',
              }}
              slots={{
                fallback:() => <CreditCard fontSize="medium" color="primary" />,
              }}
            />

            <Typography variant="h4" fontSize={16} fontWeight={600} textAlign="center">
              { dictionary.tributes.edit.card.premiumQuality }
            </Typography>

            <Typography variant="body1" fontSize={14} textAlign="center">
              { dictionary.tributes.edit.card.durable }
            </Typography>
          </Stack>
        </Grid>

        <Grid size={4}>
          <Stack alignItems="center" gap={2}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                backgroundColor: '#feeeed',
              }}
              slots={{
                fallback:() => <Star fontSize="medium" color="primary" />,
              }}
            />

            <Typography variant="h4" fontSize={16} fontWeight={600} textAlign="center">
              { dictionary.tributes.edit.card.noApp }
            </Typography>

            <Typography variant="body1" fontSize={14} textAlign="center">
              { dictionary.tributes.edit.card.workSeamlessly }
            </Typography>
            <Typography variant="body1" fontSize={10} textAlign="center" color="text.secondary">
              { dictionary.tributes.edit.card.androidDevices }
            </Typography>
          </Stack>
        </Grid>

        <Grid size={4}>
          <Stack alignItems="center" gap={2}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                backgroundColor: '#feeeed',
              }}
              slots={{
                fallback:() => <LocalShipping fontSize="medium" color="primary" />,
              }}
            />

            <Typography variant="h4" fontSize={16} fontWeight={600} textAlign="center">
              { dictionary.tributes.edit.card.fastDelivery }
            </Typography>

            <Typography variant="body1" fontSize={14} textAlign="center">
              { dictionary.tributes.edit.card.packaged }
            </Typography>
          </Stack>
        </Grid>
      </Grid>

      <Stack direction="row" justifyContent="center" mt={4} position="relative">
        <Button
          variant="contained"
          color="primary"
          type="button"
          size="small"
          onClick={onCreateCard}
          sx={{
            px: 3,
            py: 1,
            fontWeight: 700,
            fontSize: 18,
          }}
        >
          Order FREE LGCY Card
        </Button>
      </Stack>

      <Stack direction="row" justifyContent="center" mt={2} position="relative">
        <Typography variant="body1" fontSize={14} color="text.secondary" textAlign="center">
          { dictionary.tributes.edit.card.freeShipping }
        </Typography>
      </Stack>
    </Card>
  );
};
